import { useCallback, useState } from 'react';

// ── הזמנה עם תתי-פרויקטים, בכל המסכים (אלי, 30/09/2026) ──────────────────
//
// „כל פרויקט כזה שמחולק לתתי פרויקט ניתן לקבץ ולהרחיב בכל המסכים הנדרשים
// כמו הנדסה, ייצור, ניהול, רכש ועוד." ואחר כך: „זה בכל המסכים."
//
// כלל אחד לכל המסכים, כדי שלא יהיו חמש הגדרות שונות ל„אותה הזמנה":
//   · המשפחה נגזרת מהמספר: 260250, 260250.01, 260250.01.30 ← הזמנה 260250.
//     אותו כלל של עץ הפרויקטים (`parseProjectNumber` בשרת).
//   · כותרת רק למשפחה — יותר משורה אחת, או תת-פרויקט (מספר עם נקודה).
//     פרויקט בודד אינו צריך כותרת מעליו.
//   · שורות אותה הזמנה נצמדות, **במקום שבו ההזמנה הופיעה לראשונה** — המיון
//     של המסך נשמר, ורק בני המשפחה נמשכים אליה.
//   · ברירת המחדל: פתוח (אלי 30/09: „כותרת + תתים פתוחים"). מה שקופל נזכר
//     לכל מסך בנפרד, בדפדפן של מי שקיפל.

export const orderBase = (number: string): string => number.split('.')[0] ?? number;

export type OrderChunk<T> = {
  base: string;
  /** האם זו הזמנה עם סעיפים — ואז יש לה כותרת. */
  family: boolean;
  rows: T[];
};

export function chunkByOrder<T>(rows: T[], numberOf: (r: T) => string): OrderChunk<T>[] {
  const order: string[] = [];
  const byBase = new Map<string, T[]>();
  for (const r of rows) {
    const b = orderBase(numberOf(r));
    if (!byBase.has(b)) { byBase.set(b, []); order.push(b); }
    byBase.get(b)!.push(r);
  }
  return order.map((base) => {
    const rs = byBase.get(base)!;
    return { base, rows: rs, family: rs.length > 1 || rs.some((r) => numberOf(r).includes('.')) };
  });
}

/** השורות בסדר התצוגה: בני משפחה צמודים. לשימוש במקום שבו הסדר קובע (העברה למעלה/למטה). */
export function orderAdjacent<T>(rows: T[], numberOf: (r: T) => string): T[] {
  return chunkByOrder(rows, numberOf).flatMap((c) => c.rows);
}

const KEY = (screen: string): string => `edal.foldedOrders.${screen}`;

function read(screen: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(KEY(screen));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** אילו הזמנות מקופלות במסך הזה — נזכר בדפדפן, ובלי אחסון פשוט נפתח מחדש. */
export function useFoldedOrders(screen: string): { folded: Set<string>; toggle: (base: string) => void } {
  const [folded, setFolded] = useState<Set<string>>(() => read(screen));
  const toggle = useCallback((base: string) => {
    setFolded((cur) => {
      const next = new Set(cur);
      if (!next.delete(base)) next.add(base);
      try { window.localStorage.setItem(KEY(screen), JSON.stringify([...next])); } catch { /* אחסון חסום — הקיפול נשאר לפגישה הזו */ }
      return next;
    });
  }, [screen]);
  return { folded, toggle };
}
